<template>
  <main role="main" class="py-3">
    <b-container class="container-x">
      <!-- Title Section -->
      <div class="d-flex flex-row align-items-center justify-content-between mb-3 border-bottom pb-2">
        <h5 class="m-0 text-dark font-weight-bold">{{ $t("managePrivacy") }}</h5>
        <b-button variant="primary" size="sm" class="d-flex align-items-center px-3 btn-create-custom" @click="onCreateNew">
          <b-icon icon="plus" class="mr-1"></b-icon>
          {{ $t("createNew") }}
        </b-button>
      </div>

      <!-- Policy List -->
      <div class="policy-list">
        <b-card v-for="(policy, index) in policies" :key="index" class="policy-card border mb-3" no-body>
          <div class="card-header d-flex justify-content-between align-items-center bg-transparent border-bottom-0 pt-3 px-3 pb-0">
            <h6 class="m-0 font-weight-bold text-dark policy-title">{{ policy.title }}</h6>
            <div class="d-flex align-items-center">
              <b-dropdown variant="link" toggle-class="text-decoration-none p-0" no-caret right>
                <template #button-content>
                  <b-icon icon="chevron-down" class="text-secondary" font-scale="0.9"></b-icon>
                </template>
                <b-dropdown-item @click="onEdit(policy)">{{ $t("update") }}</b-dropdown-item>
                <b-dropdown-item @click="onDelete(policy)" variant="danger">{{ $t("actionDelete") }}</b-dropdown-item>
              </b-dropdown>
            </div>
          </div>

          <b-card-body class="px-3 pb-3 pt-2">
            <div class="row">
              <!-- Left Column: Info List -->
              <div class="col-md-6 border-right-custom">
                <ul class="policy-info-list list-unstyled m-0">
                  <li class="d-flex align-items-center">
                    <span class="info-label mr-2 text-secondary small font-weight-bold">{{ $t("privacyID") }}:</span> 
                    <span class="info-value text-muted">{{ policy.id }}</span>
                  </li>
                  <li class="d-flex align-items-center">
                    <span class="info-label mr-2 text-secondary small font-weight-bold">{{ $t("privacyRefNo") }}:</span> 
                    <span class="info-value text-dark">{{ policy.refNo }}</span>
                  </li>
                  <li class="d-flex align-items-center">
                    <span class="info-label mr-2 text-secondary small font-weight-bold">{{ $t("privacyVersion") }}:</span> 
                    <span class="info-value text-dark">{{ policy.version }}</span>
                  </li>
                  <li class="d-flex align-items-center">
                    <span class="info-label mr-2 text-secondary small font-weight-bold">{{ $t("privacyCreatedAt") }}:</span> 
                    <span class="info-value text-dark">{{ policy.createdDate }}</span>
                  </li>
                  <li class="d-flex align-items-center">
                    <span class="info-label mr-2 text-secondary small font-weight-bold">{{ $t("privacyUpdatedAt") }}:</span> 
                    <span class="info-value text-dark">{{ policy.updatedDate }}</span>
                  </li>
                </ul>
                <div class="mt-3 verification-check d-flex align-items-center bg-light p-2.5 rounded">
                  <b-form-checkbox v-model="policy.requireVerification" class="custom-chk mr-2"></b-form-checkbox>
                  <span class="verify-label text-dark font-weight-bold small">{{ $t("privacyRequireVerify") }}</span>
                </div>
              </div>

              <!-- Right Column: Content Preview & Links -->
              <div class="col-md-6 pl-md-3 mt-2 mt-md-0">
                <div class="info-item mb-2">
                  <div class="text-secondary small font-weight-bold mb-1">{{ $t("privacyContentLink") }}:</div>
                  <a :href="policy.contentLink" target="_blank" class="text-link text-break">{{ policy.contentLink }}</a>
                </div>
                <div class="info-item">
                  <div class="text-secondary small font-weight-bold mb-1">{{ $t("privacyLatestUpdate") }}:</div>
                  <div class="content-preview p-2 border rounded text-muted">
                    {{ policy.latestContent }}
                  </div>
                </div>
              </div>
            </div>
          </b-card-body>
        </b-card>
      </div>
    </b-container>
  </main>
</template>

<script lang="ts">
import { Component } from 'vue-property-decorator';
import BaseComponent from "@/components/BaseComponent";
import * as policyService from "@/service/policyService";
import * as util from "@/service/util";

@Component({})
export default class ManagePrivacy extends BaseComponent {
  policies: any[] = [];

  isSubmitting = false;

  mounted() {
    this.fetchPolicies();
  }

  async fetchPolicies() {
    try {
      this.showLoading();
      const res = await policyService.getAll();
      const list = Array.isArray(res) ? res : (res && Array.isArray(res.data) ? res.data : []);
      
      this.policies = list.map((item: any) => ({
        title: item.title,
        id: item.id,
        refNo: item.legalReference,
        version: item.versionTag,
        createdDate: util.localDateTimeFormat(item.createdAt),
        updatedDate: item.modifiedAt ? util.localDateTimeFormat(item.modifiedAt) : "",
        contentLink: item.fullContent && (item.fullContent.startsWith("http://") || item.fullContent.startsWith("https://")) ? item.fullContent : "-",
        latestContent: item.summaryChanges || "-",
        requireVerification: item.isActive === 1
      }));
    } catch (e) {
      console.log(e);
      this.toastError(e);
    } finally {
      this.hideLoading();
    }
  }

  onCreateNew() {
    this.$router.push("/manage-privacy/create");
  }

  onEdit(policy) {
    this.$router.push("/manage-privacy/update/" + policy.id);
  }

  onDelete(policy) {
    this.toastSuccess(this.$t("alert").toString(), `${this.$t("actionDelete")} for: ${policy.title}`);
  }
}
</script>

<style lang="scss" scoped>
.container-x {
  max-width: 1000px;
}

.policy-card {
  border-radius: 8px;
  background-color: #f4f9ff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.policy-title {
  font-size: 1.1rem;
  letter-spacing: -0.01em;
}

.policy-info-list {
  li {
    margin-bottom: 6px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
}

.info-label {
  min-width: 130px;
  display: inline-block;
}

.info-value {
  font-size: 0.9rem;
}

.content-preview {
  font-size: 0.85rem;
  line-height: 1.6;
  max-height: 150px;
  overflow-y: auto;
  text-align: justify;
}

.text-link {
  color: #007bff;
  text-decoration: none;
  font-size: 0.9rem;
  
  &:hover {
    text-decoration: underline;
  }
}

.font-weight-bold {
  font-weight: 600 !important;
}

@media (min-width: 768px) {
  .border-right-custom {
    border-right: 1px solid rgba(0, 0, 0, 0.08);
  }
}
</style>
